/* eslint-disable react-refresh/only-export-components */
import styled from '@emotion/styled'
import type { ReactNode } from 'react'

import { Heading, type HeadingSize } from './Heading'
import { KlimbBadge, type KlimbBadgeVariant } from './KlimbBadge'
import { KlimbLockup } from './KlimbLockup'
import { Row } from './Stack'
import { SurfaceRoot, type SurfaceRootProps } from './SurfaceRoot'

// AreaShell — common chrome for each Klimb area, exposed as a compound
// component so consumers can swap any slot. The convenience prop API on
// `AreaShell` covers the common case; reach for the subcomponents
// (`AreaShell.Root`, `.Header`, `.Main`, `.Footer`) when you need full
// control over layout (e.g. adding a side nav).

const ShellLayout = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  padding: var(--s-7) var(--s-8);
  gap: var(--s-9);
`

const ShellHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--s-6);
`

const ShellMain = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--s-7);
  text-align: center;
  max-width: 920px;
  margin: 0 auto;
`

const ShellFooterEl = styled.footer`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: var(--font-mono);
  font-size: 12px;
  letter-spacing: 0.08em;
  color: var(--c-fg-subtle);
`

const Subtitle = styled.div`
  font-size: var(--t-body-lg);
  color: var(--c-fg-muted);
  max-width: 640px;
  line-height: 1.5;
`

export interface AreaShellRootProps extends Omit<SurfaceRootProps, 'children'> {
  children: ReactNode
}

function AreaShellRoot({ motif = 'topo', style, children, ...rest }: AreaShellRootProps) {
  return (
    <SurfaceRoot motif={motif} style={{ minHeight: '100vh', ...style }} {...rest}>
      <ShellLayout>{children}</ShellLayout>
    </SurfaceRoot>
  )
}

export interface AreaShellProps extends Omit<SurfaceRootProps, 'title' | 'children'> {
  badge: string
  variant: KlimbBadgeVariant
  title: ReactNode
  titleSize?: HeadingSize
  children: ReactNode
  footer?: ReactNode
  footerLeft?: ReactNode
  footerRight?: ReactNode
}

// The convenience top-level component: pass `badge / title / children` and
// you get the canonical Klimb area landing. For anything custom (e.g. a
// real product page with sidebar nav), use the compound subcomponents.
function AreaShellTop({
  badge,
  variant,
  title,
  titleSize = 'display-lg',
  children,
  footer,
  footerLeft,
  footerRight,
  ...rootProps
}: AreaShellProps) {
  return (
    <AreaShellRoot {...rootProps}>
      <ShellHeader>
        <KlimbLockup size="md" />
        <KlimbBadge variant={variant}>{badge}</KlimbBadge>
      </ShellHeader>

      <ShellMain>
        <Heading level={1} size={titleSize}>
          {title}
        </Heading>
        <Subtitle>{children}</Subtitle>
        {footer ? (
          <Row gap="var(--s-3)" justify="center">
            {footer}
          </Row>
        ) : null}
      </ShellMain>

      <ShellFooterEl>
        <span>{footerLeft}</span>
        <span>{footerRight}</span>
      </ShellFooterEl>
    </AreaShellRoot>
  )
}

export const AreaShell = Object.assign(AreaShellTop, {
  Root: AreaShellRoot,
  Header: ShellHeader,
  Main: ShellMain,
  Footer: ShellFooterEl,
  Subtitle,
})
